import { useCallback, useEffect, useState } from 'react'
import {
  createDictionaryEntry,
  errorMessage,
  listDictionary,
  updateDictionaryEntry,
  uploadDictionaryAudio,
} from '../../services/admin'
import { faDigits } from '../../lib/numerals'
import { formatDurationMs, formatDatetime } from './dates'
import {
  DICTIONARY_STATUSES,
  DICTIONARY_STATUS_CLS,
  DICTIONARY_STATUS_FA,
  dictionaryRefusal,
  hasPersianContent,
} from './dictionary'

// ─── Dictionary tab (admin-dictionary, V2-p) ──────────────────────────────────

const inputCls = 'rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none'
const btnPrimary = 'rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50'
const btnGhost = 'rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-40'

const PAGE = 50

export function DictionaryStatusBadge({ status }) {
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${DICTIONARY_STATUS_CLS[status] ?? 'bg-slate-100 text-slate-500'}`}>
      {DICTIONARY_STATUS_FA[status] ?? status}
    </span>
  )
}

/** The examples list of an entry: rows of English + Persian, add and remove. */
function ExamplesEditor({ examples, onChange }) {
  const setRow = (i, k, v) => onChange(examples.map((row, j) => (j === i ? { ...row, [k]: v } : row)))
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-slate-600">مثال‌ها</span>
        <button type="button" onClick={() => onChange([...examples, { textEn: '', textFa: '' }])}
          className="rounded px-2 py-0.5 text-xs text-brand-700 hover:bg-brand-50">+ افزودن مثال</button>
      </div>
      {examples.map((row, i) => (
        <div key={i} className="flex items-center gap-2">
          <input value={row.textEn} dir="ltr" placeholder="I need a coffee." className={`${inputCls} flex-1`}
            onChange={e => setRow(i, 'textEn', e.target.value)} />
          <input value={row.textFa} dir="rtl" placeholder="من یک قهوه لازم دارم." className={`${inputCls} flex-1`}
            onChange={e => setRow(i, 'textFa', e.target.value)} />
          <button type="button" onClick={() => onChange(examples.filter((_, j) => j !== i))} title="حذف مثال"
            className="text-slate-300 hover:text-red-500">✕</button>
        </div>
      ))}
      {examples.length === 0 && <p className="text-[11px] text-slate-400">مثالی ثبت نشده.</p>}
    </div>
  )
}

/**
 * The fields of one entry, for both creating and editing. `entry` null means
 * a new one. Submits the wire shape; the caller decides create vs update.
 */
export function EntryForm({ entry, busy, onSubmit, onCancel }) {
  const [headword, setHeadword] = useState(entry?.headword ?? '')
  const [meaningFa, setMeaningFa] = useState(entry?.meaningFa ?? '')
  const [translationFa, setTranslationFa] = useState(entry?.translationFa ?? '')
  const [explanationFa, setExplanationFa] = useState(entry?.explanationFa ?? '')
  const [examples, setExamples] = useState(entry?.examples ?? [])
  const [status, setStatus] = useState(entry?.status ?? 'draft')
  const [err, setErr] = useState('')

  const linked = (entry?.linkCount ?? 0) > 0

  async function submit(e) {
    e.preventDefault()
    if (!headword.trim()) { setErr('کلمه لازم است.'); return }
    const rows = examples.map(r => ({ textEn: r.textEn.trim(), textFa: r.textFa.trim() })).filter(r => r.textEn || r.textFa)
    if (rows.some(r => !r.textEn || !r.textFa)) { setErr('هر مثال هم متن انگلیسی می‌خواهد هم فارسی.'); return }
    const dto = { headword: headword.trim(), status, examples: rows }
    if (status === 'published' && !hasPersianContent({ meaningFa, translationFa, explanationFa })) {
      setErr('برای انتشار، دست‌کم یکی از معنی، ترجمه یا توضیح را پر کنید.'); return
    }
    if (entry) {
      dto.meaningFa = meaningFa.trim() || null
      dto.translationFa = translationFa.trim() || null
      dto.explanationFa = explanationFa.trim() || null
    } else {
      if (meaningFa.trim()) dto.meaningFa = meaningFa.trim()
      if (translationFa.trim()) dto.translationFa = translationFa.trim()
      if (explanationFa.trim()) dto.explanationFa = explanationFa.trim()
    }
    setErr('')
    await onSubmit(dto)
  }

  const FL = 'mb-1 block text-xs font-medium text-slate-600'

  return (
    <form onSubmit={submit} className="space-y-3 rounded-lg bg-slate-50 px-3 py-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <label className="block">
          <span className={FL}>کلمه *</span>
          <input required value={headword} dir="ltr" placeholder="coffee" className={`w-full ${inputCls}`}
            onChange={e => setHeadword(e.target.value)} />
          {linked && <span className="mt-1 block text-[10px] text-slate-400">به {faDigits(entry.linkCount)} جمله وصل است؛ فقط املا و بزرگی حروف را می‌شود عوض کرد.</span>}
        </label>
        <label className="block">
          <span className={FL}>معنی</span>
          <input value={meaningFa} dir="rtl" placeholder="قهوه" className={`w-full ${inputCls}`} onChange={e => setMeaningFa(e.target.value)} />
        </label>
        <label className="block">
          <span className={FL}>وضعیت</span>
          <select value={status} onChange={e => setStatus(e.target.value)} className={`w-full ${inputCls}`}>
            {DICTIONARY_STATUSES.map(s => <option key={s} value={s}>{DICTIONARY_STATUS_FA[s]}</option>)}
          </select>
        </label>
      </div>
      <label className="block">
        <span className={FL}>ترجمه</span>
        <input value={translationFa} dir="rtl" className={`w-full ${inputCls}`} onChange={e => setTranslationFa(e.target.value)} />
      </label>
      <label className="block">
        <span className={FL}>توضیح</span>
        <textarea value={explanationFa} dir="rtl" rows={2} className={`w-full ${inputCls} resize-none`}
          onChange={e => setExplanationFa(e.target.value)} />
      </label>
      <ExamplesEditor examples={examples} onChange={setExamples} />
      {err && <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
      <div className="flex flex-wrap items-center gap-2">
        <button type="submit" disabled={busy} className={btnPrimary}>{entry ? 'ذخیره' : 'ساخت مدخل'}</button>
        {onCancel && <button type="button" disabled={busy} onClick={onCancel} className={btnGhost}>انصراف</button>}
        {entry && <span className="text-xs text-slate-400">حذف مدخل وجود ندارد؛ مدخل بی‌مصرف را در «پیش‌نویس» بگذارید.</span>}
      </div>
    </form>
  )
}

/** The pronunciation: play what is there, upload or replace it. */
export function AudioRow({ entry, onUpdated }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  async function pick(e) {
    const file = e.target.files?.[0]; if (!file) return
    setBusy(true); setErr('')
    try { onUpdated(await uploadDictionaryAudio(entry.id, file)) }
    catch (ex) { setErr(dictionaryRefusal(ex) ?? errorMessage(ex)) }
    finally { setBusy(false); e.target.value = '' }
  }

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-xs text-slate-500">تلفظ:</span>
      {entry.audioUrl
        ? (
          <>
            <audio controls preload="none" src={entry.audioUrl} className="h-8" />
            <span className="text-xs text-slate-400">{formatDurationMs(entry.audioDurationMs ?? 0)}</span>
          </>
        )
        : <span className="text-xs text-amber-600">ندارد</span>}
      <label className={`cursor-pointer rounded px-1.5 py-0.5 text-xs ${busy ? 'opacity-40' : 'bg-brand-50 text-brand-700 hover:bg-brand-100'}`}>
        {busy ? '…' : entry.audioUrl ? 'جایگزینی' : 'آپلود تلفظ'}
        <input type="file" accept="audio/*" className="hidden" onChange={pick} disabled={busy} />
      </label>
      {err && <span className="text-xs text-red-600">{err}</span>}
    </div>
  )
}

/** One entry in the list: its word, status, how many sentences use it; opens into the editor. */
export function EntryCard({ entry, onChanged }) {
  const [editing, setEditing] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  async function save(patch) {
    setBusy(true); setErr('')
    try { onChanged(await updateDictionaryEntry(entry.id, patch)); setEditing(false) }
    catch (ex) { setErr(dictionaryRefusal(ex) ?? errorMessage(ex)) }
    finally { setBusy(false) }
  }

  async function publish() {
    setBusy(true); setErr('')
    try { onChanged(await updateDictionaryEntry(entry.id, { status: 'published' })) }
    catch (ex) { setErr(dictionaryRefusal(ex) ?? errorMessage(ex)) }
    finally { setBusy(false) }
  }

  return (
    <li className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm" data-entry={entry.id}>
      <div className="flex flex-wrap items-center gap-2">
        <span dir="ltr" className="font-semibold text-slate-800">{entry.headword}</span>
        {entry.normalized !== entry.headword && <span dir="ltr" className="text-xs text-slate-400">{entry.normalized}</span>}
        <DictionaryStatusBadge status={entry.status} />
        <span className="text-xs text-slate-400">{faDigits(entry.linkCount)} جمله</span>
        {entry.meaningFa && <span className="truncate text-sm text-slate-600">{entry.meaningFa}</span>}
        <span className="ms-auto flex items-center gap-1">
          {entry.status !== 'published' && (
            <button type="button" disabled={busy || !hasPersianContent(entry)} onClick={publish}
              title={hasPersianContent(entry) ? 'انتشار' : 'ابتدا معنی، ترجمه یا توضیح بنویسید'}
              className="rounded-lg bg-green-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-40">
              انتشار
            </button>
          )}
          <button type="button" disabled={busy} onClick={() => setEditing(v => !v)} className={btnGhost}>ویرایش</button>
        </span>
      </div>
      <div className="mt-2"><AudioRow entry={entry} onUpdated={onChanged} /></div>
      <p className="mt-1 text-[10px] text-slate-400">به‌روزرسانی: {formatDatetime(entry.updatedAt)}</p>
      {err && <div className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{err}</div>}
      {editing && (
        <div className="mt-2">
          <EntryForm entry={entry} busy={busy} onSubmit={save} onCancel={() => setEditing(false)} />
        </div>
      )}
    </li>
  )
}

/**
 * The «واژه‌نامه» tab (V2-p): search by word, filter by status (the review
 * queue is «نیازمند بازبینی» — where V2-b-migrate's drafts land), create an
 * entry, edit one, give it its pronunciation, publish it. Every change
 * re-reads the page: the server owns `linkCount` and `updatedAt`.
 */
export function DictionaryTab() {
  const [q, setQ] = useState('')
  const [status, setStatus] = useState('')
  const [offset, setOffset] = useState(0)
  const [data, setData] = useState(null)
  const [creating, setCreating] = useState(false)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  const load = useCallback(async () => {
    setErr('')
    try { setData(await listDictionary({ q: q.trim() || undefined, status: status || undefined, limit: PAGE, offset })) }
    catch (ex) { setErr(errorMessage(ex)) }
  }, [q, status, offset])

  useEffect(() => {
    const id = setTimeout(load, 250)
    return () => clearTimeout(id)
  }, [load])

  async function create(dto) {
    setBusy(true); setErr('')
    try { await createDictionaryEntry(dto); setCreating(false); await load() }
    catch (ex) { setErr(dictionaryRefusal(ex) ?? errorMessage(ex)) }
    finally { setBusy(false) }
  }

  function replace(updated) {
    setData(d => (d ? { ...d, items: d.items.map(item => (item.id === updated.id ? updated : item)) } : d))
  }

  const total = data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / PAGE))
  const page = Math.floor(offset / PAGE)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <input value={q} onChange={e => { setQ(e.target.value); setOffset(0) }} dir="ltr" placeholder="جست‌وجوی کلمه…"
          className={`${inputCls} w-56`} />
        <select value={status} onChange={e => { setStatus(e.target.value); setOffset(0) }} className={inputCls}>
          <option value="">همهٔ وضعیت‌ها</option>
          {DICTIONARY_STATUSES.map(s => <option key={s} value={s}>{DICTIONARY_STATUS_FA[s]}</option>)}
        </select>
        <button type="button" onClick={() => { setStatus('needs_review'); setOffset(0) }}
          className={`${btnGhost} ${status === 'needs_review' ? 'bg-sky-50 text-sky-800' : ''}`}>
          صف بازبینی
        </button>
        <span className="ms-auto flex items-center gap-2">
          <button type="button" onClick={load} className={btnGhost}>تازه‌سازی</button>
          <button type="button" onClick={() => setCreating(v => !v)} className={btnPrimary}>+ مدخل تازه</button>
        </span>
      </div>

      {creating && <EntryForm entry={null} busy={busy} onSubmit={create} onCancel={() => setCreating(false)} />}
      {err && <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{err}</div>}

      {data === null
        ? <p className="py-16 text-center text-sm text-slate-400">در حال بارگذاری…</p>
        : data.items.length === 0
          ? <p className="py-16 text-center text-sm text-slate-400">{q || status ? 'مدخلی با این شرایط نیست.' : 'واژه‌نامه هنوز خالی است. از فرم درس، روی هر کلمه «پیوند» بزنید یا اینجا مدخل بسازید.'}</p>
          : (
            <>
              <p className="text-xs text-slate-500">{faDigits(total)} مدخل</p>
              <ul className="space-y-2">
                {data.items.map(entry => <EntryCard key={entry.id} entry={entry} onChanged={replace} />)}
              </ul>
              {pages > 1 && (
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <button type="button" disabled={page === 0} onClick={() => setOffset(o => Math.max(0, o - PAGE))} className={btnGhost}>قبلی</button>
                  <span>صفحهٔ {faDigits(page + 1)} از {faDigits(pages)}</span>
                  <button type="button" disabled={page >= pages - 1} onClick={() => setOffset(o => o + PAGE)} className={btnGhost}>بعدی</button>
                </div>
              )}
            </>
          )}
    </div>
  )
}
